import React, { useEffect, useState } from "react";
import "./styles.css";
import { Container, Table } from "react-bootstrap";
import { BLANK_STRING, VIEW_MODES } from "../../constants/appConstants";
import SwitchViewData from "../../Components/SwitchViewData/SwitchViewData";
import MultiSelector from "../../Components/MultiSelector/MultiSelector";
import { useNavigate, useParams } from "react-router-dom";
import { exportQuarterAndYear, findCurrentQuarter, getDifferenceDays, getQuarterString, getQuarterYearObject } from "../../utils/commonUtils";
import {
    getNonconformanceAndInvestigationKPIDataAPI,
    getNonconformanceAndInvestigationKPIDataByPeriodAPI,
} from "../../apiRequests/nonconformance-and-investigation/getNonconformanceAndInvestigationKPIDataAPI";
import MaskLoading from "../../Components/LoadingPage/MaskLoading";

interface NonconformanceAndInvestigationProps {
    isDashboard?: boolean;
    dataNAIs?: any;
}

const NonconformanceAndInvestigation = (props: NonconformanceAndInvestigationProps) => {
    const navigate = useNavigate();
    const { period } = useParams();
    const [dataNAIs, setDataNAIs] = useState<any>([]);
    const [currentViewMode, setCurrentViewMode] = useState<string>(VIEW_MODES.DETAILED);
    const [selectedYear, setSelectedYear] = useState<string>(BLANK_STRING);
    const [isRenderSelectQuarter, setIsRenderSelectQuarter] = useState<boolean>(false);
    const [selectedQuarter, setSelectedQuarter] = useState<string>(BLANK_STRING);
    const [selectedYearAndQuarter, setSelectedYearAndQuarter] = useState<string[]>([]);
    const [dataKPIForMultiQuarter, setDataKPIForMultiQuarter] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    useEffect(() => {
        !props.isDashboard && getNAIDataAndSetToState();
    }, []);
    useEffect(() => {
        const getAllNAIsData = async () => {
            const isAllDataLoaded = dataNAIs.every((data: any) => Object.values(data.quarterNAIs).every((quarterData: any) => quarterData.isLoaded));
            if (currentViewMode === VIEW_MODES.TABLE && !isAllDataLoaded) {
                setIsLoading(true);
                try {
                    const result = await getNonconformanceAndInvestigationKPIDataAPI();
                    setDataNAIs(result);
                } catch (error) {
                    console.error("Error fetching Purchase Acceptance data:", error);
                }
                setIsLoading(false);
            }
        };
        getAllNAIsData();
    }, [currentViewMode]);
    useEffect(() => {
        if (props.isDashboard) {
            setDataNAIs(props.dataNAIs);
            setCurrentYearAndQuarter(props.dataNAIs);
        }
    }, [props.dataNAIs, props.isDashboard]);

    const setCurrentYearAndQuarter = (dataNAIs: any) => {
        const { year, quarter } = exportQuarterAndYear(period || BLANK_STRING);
        if (year && quarter) {
            const currentNAI = dataNAIs.find((data: any) => data.yearNAIs.title.includes(year));
            setSelectedYear(currentNAI?.yearNAIs?.id || BLANK_STRING);
            const currentQuarter = findCurrentQuarter(currentNAI?.quarterNAIs, year, quarter);
            setSelectedQuarter(currentQuarter || BLANK_STRING);
            setSelectedYearAndQuarter([currentQuarter || BLANK_STRING]);
        } else {
            const currentYear = new Date().getFullYear();
            const currentNAI = dataNAIs.find((data: any) => data.yearNAIs.title.includes(currentYear));
            setSelectedYear(currentNAI?.yearNAIs?.id);
            const currentQuarter = Math.floor(new Date().getMonth() / 3) + 1;
            const selectedQuarter = findCurrentQuarter(currentNAI?.quarterNAIs, currentYear, currentQuarter);
            setSelectedQuarter(selectedQuarter || BLANK_STRING);
            const quarterString = getQuarterString(currentQuarter);

            setSelectedYearAndQuarter([`NC Q${currentQuarter},${currentYear}`]);
            !props.isDashboard && navigate(`/nonconformance-and-investigation/${quarterString}-${currentYear}`, { replace: true });
        }
    };

    useEffect(() => {
        if (selectedYear) {
            setIsRenderSelectQuarter(true);
        }
    }, [selectedYear]);

    useEffect(() => {
        const quarterData = dataNAIs.reduce((acc: any[], item: any) => {
            const selectedQuarters = Object.keys(item.quarterNAIs)
                .filter((quarterKey: string) => selectedYearAndQuarter.includes(quarterKey))
                .map((quarterKey: string) => ({ [quarterKey]: item.quarterNAIs[quarterKey].data }));

            return [...acc, ...selectedQuarters];
        }, []);

        if (quarterData) {
            const calculatedData = quarterData.map((data: any, index: number) => {
                const { KPI, passed, total } = calculateMetrics(data[selectedYearAndQuarter[index]]);

                return { KPI, passed, total };
            });
            setDataKPIForMultiQuarter(calculatedData);
        }
    }, [selectedYearAndQuarter]);

    const getNAIDataAndSetToState = async () => {
        setIsLoading(true);
        const { year: periodYear, quarter: periodQuarter } = exportQuarterAndYear(period || BLANK_STRING);
        const targetYear = periodYear || new Date().getFullYear();
        const targetQuarter = periodQuarter || Math.floor(new Date().getMonth() / 3) + 1;
        const dataNAIs: any = await getNonconformanceAndInvestigationKPIDataByPeriodAPI(targetYear, targetQuarter);
        setDataNAIs(dataNAIs);
        if (dataNAIs.length) {
            setCurrentYearAndQuarter(dataNAIs);
        }
        setIsLoading(false);
    };

    const renderSelectNAIsYear = () => {
        return (
            <div className="selector-container">
                <label className="label" htmlFor="year">
                    Select Year:
                </label>
                <select
                    className="select"
                    id="year"
                    value={selectedYear}
                    onChange={(event) => {
                        setSelectedYear(event.target.value);
                    }}
                >
                    <option disabled value="">
                        Select Year
                    </option>
                    {dataNAIs.map((data: any) => (
                        <option key={data.yearNAIs.title} value={data.yearNAIs.id}>
                            {data.yearNAIs.title}
                        </option>
                    ))}
                </select>
            </div>
        );
    };

    const handleQuarterChange = async (event: any) => {
        setIsLoading(true);
        setSelectedQuarter(event.target.value);
        const { quarter, year } = getQuarterYearObject(event.target.value);

        if (!Boolean(dataNAIs.find((data: any) => data.yearNAIs.id === selectedYear).quarterNAIs[event.target.value].isLoaded)) {
            const result: any = await getNonconformanceAndInvestigationKPIDataByPeriodAPI(year, quarter);
            const mergedData = mergeDataNAIs(dataNAIs, result);
            setDataNAIs(mergedData);
        }

        const quarterInString = getQuarterString(quarter);
        !props.isDashboard && navigate(`/nonconformance-and-investigation/${quarterInString}-${year}`, { replace: true });
        setIsLoading(false);
    };

    function mergeDataNAIs(array1: any, array2: any) {
        const result: any = [];

        array1.forEach((item1: any, index: any) => {
            const item2 = array2[index];

            const mergedItem: any = {
                yearNAIs: item1.yearNAIs,
                quarterNAIs: {},
            };

            const allQuarters = new Set([...Object.keys(item1.quarterNAIs), ...Object.keys(item2.quarterNAIs)]);

            allQuarters.forEach((quarter) => {
                const data1 = item1.quarterNAIs[quarter]?.data || [];
                const data2 = item2.quarterNAIs[quarter]?.data || [];
                const isLoaded1 = item1.quarterNAIs[quarter]?.isLoaded || false;
                const isLoaded2 = item2.quarterNAIs[quarter]?.isLoaded || false;

                mergedItem.quarterNAIs[quarter] = {
                    data: [...data1, ...data2],
                    isLoaded: isLoaded1 || isLoaded2,
                };
            });

            result.push(mergedItem);
        });

        return result;
    }

    const renderSelectNAIsQuarter = () => {
        return (
            <div className="selector-container">
                <label className="label" htmlFor="quarter">
                    Select Quarter:
                </label>
                <select
                    className="select"
                    id="quarter"
                    value={selectedQuarter}
                    onChange={(event) => {
                        handleQuarterChange(event);
                    }}
                >
                    <option value="">Select Quarter</option>
                    {dataNAIs.map((data: any) => {
                        if (data.yearNAIs.id === selectedYear) {
                            return Object.keys(data.quarterNAIs).map((quarter) => (
                                <option key={quarter} value={quarter}>
                                    {quarter}
                                </option>
                            ));
                        }
                    })}
                </select>
            </div>
        );
    };

    const calculateMetrics = (dataNAIsInSelectedQuarter: any[]) => {
        const passed = dataNAIsInSelectedQuarter.reduce((acc: number, data: any) => {
            if (data.status === "PASSED") {
                return acc + 1;
            }
            return acc;
        }, 0);
        const total = dataNAIsInSelectedQuarter.length;
        const KPI = ((passed / total) * 100).toFixed(2);

        return { KPI, passed, total };
    };

    const renderNAIsDataTable = () => {
        const dataNAIsInSelectedYear = dataNAIs.find((data: any) => data.yearNAIs.id === selectedYear);
        const dataNAIsInSelectedQuarter = dataNAIsInSelectedYear.quarterNAIs[selectedQuarter].data;
        const { KPI, passed, total } = calculateMetrics(dataNAIsInSelectedQuarter);

        return (
            <Table striped bordered hover>
                <thead>
                    <tr>
                        <th>KPI</th>
                        <th>PASSED</th>
                        <th>FAILED</th>
                        <th>TOTAL</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td>{KPI}%</td>
                        <td>{passed}</td>
                        <td>{total - passed}</td>
                        <td>{total}</td>
                    </tr>
                </tbody>
            </Table>
        );
    };

    const renderNAIsFailed = () => {
        const dataNAIsInSelectedYear = dataNAIs.find((data: any) => data.yearNAIs.id === selectedYear);
        const dataNAIsInSelectedQuarter = dataNAIsInSelectedYear.quarterNAIs[selectedQuarter].data;
        const failedNAIs = dataNAIsInSelectedQuarter.filter((data: any) => data.status === "FAILED");
        return (
            Boolean(failedNAIs.length) && (
                <Container>
                    <p>
                        <b>Failed NAIs</b>
                    </p>
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Title</th>
                                <th>Discovered Date</th>
                                <th>NAI Effective on</th>
                                <th>Difference days</th>
                            </tr>
                        </thead>
                        <tbody>
                            {failedNAIs.map((data: any, index: number) => (
                                <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>
                                        <a target="_blank" href={data.link}>
                                            {data.title}
                                        </a>
                                    </td>
                                    <td>{new Date(data.discoveredDate).toLocaleDateString()}</td>
                                    <td>{new Date(data.effectiveOnNAI).toLocaleDateString()}</td>
                                    <td>{getDifferenceDays(data.discoveredDate, data.effectiveOnNAI)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Container>
            )
        );
    };

    const renderNAIsPassed = () => {
        const dataNAIsInSelectedYear = dataNAIs.find((data: any) => data.yearNAIs.id === selectedYear);
        const dataNAIsInSelectedQuarter = dataNAIsInSelectedYear.quarterNAIs[selectedQuarter].data;
        const passedNAIs = dataNAIsInSelectedQuarter.filter((data: any) => data.status === "PASSED");
        return (
            Boolean(passedNAIs.length) && (
                <Container>
                    <p>
                        <b>Passed NAIs</b>
                    </p>
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Title</th>
                                <th>Discovered Date</th>
                                <th>NAI Effective on</th>
                                <th>Difference days</th>
                            </tr>
                        </thead>
                        <tbody>
                            {passedNAIs.map((data: any, index: number) => (
                                <tr key={index}>
                                    <td>{index + 1}</td>
                                    <td>
                                        <a target="_blank" href={data.link}>
                                            {data.title}
                                        </a>
                                    </td>
                                    <td>{new Date(data.discoveredDate).toLocaleDateString()}</td>
                                    <td>{new Date(data.effectiveOnNAI).toLocaleDateString()}</td>
                                    <td>{getDifferenceDays(data.discoveredDate, data.effectiveOnNAI)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Container>
            )
        );
    };

    const renderLoading = () => {
        return <MaskLoading></MaskLoading>;
    };
    function extractAllQuarterKeys(data: any[] = dataNAIs) {
        const allQuarterKeys: any = [];
        data.forEach((item) => {
            if (item && item.quarterNAIs) {
                const quarterKeys = Object.keys(item.quarterNAIs);
                allQuarterKeys.push(...quarterKeys);
            }
        });
        return allQuarterKeys.sort(sortStringQuarterAndYear);
    }

    const sortStringQuarterAndYear = (a: string, b: string): number => {
        const [quarterA, yearA] = a.split(",").map((part, index) => (index === 0 ? part.slice(-1) : part));
        const [quarterB, yearB] = b.split(",").map((part, index) => (index === 0 ? part.slice(-1) : part));

        if (yearA > yearB) {
            return -1;
        } else if (yearA < yearB) {
            return 1;
        } else {
            return parseInt(quarterB) - parseInt(quarterA);
        }
    };

    const renderTableMultiQuarter = () => {
        return (
            <Table>
                <thead>
                    <tr>
                        <th></th>
                        <th>KPI</th>
                        <th>PASSED</th>
                        <th>FAILED</th>
                        <th>TOTAL</th>
                    </tr>
                </thead>
                <tbody>
                    {dataKPIForMultiQuarter.map((data: any, index: number) => (
                        <tr key={selectedYearAndQuarter[index]}>
                            <td>{selectedYearAndQuarter[index]}</td>
                            <td>{data.KPI}%</td>
                            <td>{data.passed}</td>
                            <td>{data.total - data.passed}</td>
                            <td>{data.total}</td>
                        </tr>
                    ))}
                </tbody>
            </Table>
        );
    };

    const renderDetailedView = () => {
        return (
            <>
                {renderSelectNAIsYear()}
                {isRenderSelectQuarter && renderSelectNAIsQuarter()}
                {selectedQuarter && !isLoading && renderNAIsDataTable()}
                {selectedQuarter && !isLoading && renderNAIsFailed()}
                {selectedQuarter && !isLoading && renderNAIsPassed()}
            </>
        );
    };

    const renderTableViewMultiQuarter = () => {
        return (
            <>
                <MultiSelector
                    dataOptions={extractAllQuarterKeys()}
                    currentSelected={selectedYearAndQuarter.sort(sortStringQuarterAndYear)}
                    onChange={(data: any[]) => {
                        setSelectedYearAndQuarter(data.sort(sortStringQuarterAndYear));
                    }}
                    title="Select Year And Quarter"
                ></MultiSelector>
                {selectedYearAndQuarter.length > 0 && renderTableMultiQuarter()}
            </>
        );
    };

    const renderMainContentOfNonconformanceAndInvestigation = () => {
        return (
            <>
                {isLoading && renderLoading()}
                <SwitchViewData currentViewMode={currentViewMode} setCurrentViewMode={(view) => setCurrentViewMode(view)}></SwitchViewData>
                {currentViewMode === VIEW_MODES.DETAILED && renderDetailedView()}
                {currentViewMode === VIEW_MODES.TABLE && renderTableViewMultiQuarter()}
            </>
        );
    };
    return <Container>{renderMainContentOfNonconformanceAndInvestigation()}</Container>;
};

export default NonconformanceAndInvestigation;
